import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { RigidBody } from "@react-three/rapier";
import * as THREE from "three";
import {
  mulberry32,
  getBarkTexture,
  getLeafTexture,
  getRockTexture,
  getMountainTexture,
  getCloudTexture,
} from "./textures";

const WORLD_RADIUS = 38; // driveable area (inside boundary walls at 40)
const CLEAR_RADIUS = 5; // keep spawn clear
const MARKER_CLEARANCE = 5.5; // generous enough to clear cave archways too

interface SceneryProps {
  markerPositions: [number, number, number][];
}

const Tree: React.FC<{ position: [number, number, number]; scale: number; variant: number }> = ({
  position,
  scale,
  variant,
}) => {
  const bark = getBarkTexture();
  const leaf = getLeafTexture(variant);
  return (
    <RigidBody type="fixed" colliders="cuboid" position={position}>
      <group scale={scale}>
        <mesh position={[0, 0.7, 0]} castShadow>
          <cylinderGeometry args={[0.15, 0.2, 1.4, 6]} />
          <meshStandardMaterial map={bark} roughness={1} />
        </mesh>
        <mesh position={[0, 1.9, 0]} castShadow>
          <coneGeometry args={[1.1, 1.8, 7]} />
          <meshStandardMaterial map={leaf} roughness={1} />
        </mesh>
        <mesh position={[0, 2.7, 0]} castShadow>
          <coneGeometry args={[0.75, 1.2, 7]} />
          <meshStandardMaterial map={leaf} roughness={1} />
        </mesh>
      </group>
    </RigidBody>
  );
};

const Rock: React.FC<{ position: [number, number, number]; scale: number }> = ({
  position,
  scale,
}) => {
  const rock = getRockTexture();
  return (
    <RigidBody type="fixed" colliders="cuboid" position={position}>
      <mesh scale={scale} castShadow receiveShadow rotation={[0.3, 0.6, 0.1]}>
        <dodecahedronGeometry args={[0.6, 0]} />
        <meshStandardMaterial map={rock} roughness={1} flatShading />
      </mesh>
    </RigidBody>
  );
};

const Mountain: React.FC<{ position: [number, number, number]; height: number; radius: number }> = ({
  position,
  height,
  radius,
}) => {
  const tex = getMountainTexture();
  return (
    <mesh position={position} castShadow>
      <coneGeometry args={[radius, height, 5]} />
      <meshStandardMaterial map={tex} roughness={1} flatShading />
    </mesh>
  );
};

const Clouds: React.FC = () => {
  const groupRef = useRef<THREE.Group>(null);
  const cloudTex = getCloudTexture();

  const clouds = useMemo(() => {
    const rand = mulberry32(99);
    return new Array(9).fill(0).map((_, i) => ({
      pos: [
        (rand() - 0.5) * 70,
        13 + rand() * 6,
        (rand() - 0.5) * 70,
      ] as [number, number, number],
      scale: 3 + rand() * 3,
      speed: 0.15 + rand() * 0.2,
      key: i,
    }));
  }, []);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    groupRef.current.children.forEach((child, i) => {
      child.position.x += clouds[i].speed * delta;
      if (child.position.x > 45) child.position.x = -45;
    });
  });

  return (
    <group ref={groupRef}>
      {clouds.map((c) => (
        <sprite key={c.key} position={c.pos} scale={[c.scale * 1.6, c.scale, 1]}>
          <spriteMaterial map={cloudTex} transparent depthWrite={false} />
        </sprite>
      ))}
    </group>
  );
};

const Scenery: React.FC<SceneryProps> = ({ markerPositions }) => {
  const { trees, rocks, mountains } = useMemo(() => {
    const rand = mulberry32(42);
    const CORRIDOR_HALF_WIDTH = 2.2;

    // Distance from point (x,z) to the segment from origin to a marker
    const distToApproachCorridor = (x: number, z: number, mx: number, mz: number) => {
      const len2 = mx * mx + mz * mz;
      if (len2 === 0) return Infinity;
      const t = Math.max(0, Math.min(1, (x * mx + z * mz) / len2));
      const px = mx * t;
      const pz = mz * t;
      return Math.hypot(x - px, z - pz);
    };

    const isClear = (x: number, z: number) => {
      if (Math.hypot(x, z) < CLEAR_RADIUS) return false;
      return !markerPositions.some(([mx, , mz]) => {
        if (Math.hypot(x - mx, z - mz) < MARKER_CLEARANCE) return true;
        return distToApproachCorridor(x, z, mx, mz) < CORRIDOR_HALF_WIDTH;
      });
    };

    const trees: { pos: [number, number, number]; scale: number; variant: number }[] = [];
    let attempts = 0;
    while (trees.length < 55 && attempts < 400) {
      attempts++;
      const x = (rand() - 0.5) * WORLD_RADIUS * 2;
      const z = (rand() - 0.5) * WORLD_RADIUS * 2;
      if (Math.hypot(x, z) > WORLD_RADIUS - 2) continue;
      if (!isClear(x, z)) continue;
      trees.push({
        pos: [x, 0, z],
        scale: 0.8 + rand() * 0.7,
        variant: Math.floor(rand() * 3),
      });
    }

    const rocks: { pos: [number, number, number]; scale: number }[] = [];
    attempts = 0;
    while (rocks.length < 22 && attempts < 300) {
      attempts++;
      const x = (rand() - 0.5) * WORLD_RADIUS * 2;
      const z = (rand() - 0.5) * WORLD_RADIUS * 2;
      if (Math.hypot(x, z) > WORLD_RADIUS - 2) continue;
      if (!isClear(x, z)) continue;
      rocks.push({ pos: [x, 0.2, z], scale: 0.5 + rand() * 0.6 });
    }

    const mountains: { pos: [number, number, number]; height: number; radius: number }[] = [];
    const mountainCount = 18;
    for (let i = 0; i < mountainCount; i++) {
      let angle = (i / mountainCount) * Math.PI * 2 + rand() * 0.2;
      const dist = 46 + rand() * 10;
      const radius = 6 + rand() * 5;

      // Mountains sit just outside the walls, but corner caves (and other avoid
      // points near the edge) can still poke into a mountain's footprint — nudge
      // the angle away until it's clear, rather than leaving a big peak looming
      // right behind/through an entrance.
      let nudges = 0;
      while (nudges < 12) {
        const x = Math.cos(angle) * dist;
        const z = Math.sin(angle) * dist;
        const blocked = markerPositions.some(
          ([mx, , mz]) => Math.hypot(x - mx, z - mz) < radius + 8,
        );
        if (!blocked) break;
        angle += Math.PI / mountainCount; // shift by half a slot
        nudges++;
      }

      mountains.push({
        pos: [Math.cos(angle) * dist, 0, Math.sin(angle) * dist],
        height: 14 + rand() * 16,
        radius,
      });
    }

    return { trees, rocks, mountains };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <group>
      {trees.map((t, i) => (
        <Tree key={`tree-${i}`} position={t.pos} scale={t.scale} variant={t.variant} />
      ))}
      {rocks.map((r, i) => (
        <Rock key={`rock-${i}`} position={r.pos} scale={r.scale} />
      ))}
      {mountains.map((m, i) => (
        <Mountain key={`mountain-${i}`} position={m.pos} height={m.height} radius={m.radius} />
      ))}
      <Clouds />
    </group>
  );
};

export default Scenery;
